import React, { useState, useEffect } from 'react';
import { getOfferingProducts, addOfferingProduct, deleteOfferingProduct, uploadOfferingImage } from '../../api/offerings';
import ConfirmationModal from '../Shared/ConfirmationModal';
import CustomDropdown from '../Shared/CustomDropdown';
import './OfferingProductsManager.css';

const DELIVERY_METHOD_OPTIONS = [
  { value: 'at_event', label: 'Provided at service / Event' },
  { value: 'pre_ship', label: 'Pre-shipped to address' },
  { value: 'digital', label: 'Digital download links' }
];

export default function OfferingProductsManager({ offeringId }) {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Image uploading states
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState('at_event'); // pre_ship | at_event | digital
  const [includedInPrice, setIncludedInPrice] = useState(true);
  const [addonPrice, setAddonPrice] = useState('');
  const [digitalFileUrl, setDigitalFileUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  // Delete modal state
  const [selectedProductForDelete, setSelectedProductForDelete] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [offeringId]);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const data = await getOfferingProducts(offeringId);
      setProducts(data || []);
    } catch (err) {
      console.error("Failed to load products", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 3 * 1024 * 1024) {
      alert("Product image must be under 3MB");
      return;
    }

    setIsUploadingImage(true);
    try {
      const res = await uploadOfferingImage(file);
      setImageUrl(res.url);
    } catch (err) {
      console.error("Failed to upload product image", err);
      alert("Image upload failed. Please try again.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      sku: sku.trim() || null,
      quantity: parseInt(quantity) || 0,
      deliveryMethod,
      includedInPrice,
      addonPrice: includedInPrice ? null : (parseFloat(addonPrice) || 0),
      digitalFileUrl: deliveryMethod === 'digital' ? (digitalFileUrl.trim() || null) : null,
      imageUrl: imageUrl || null
    };

    try {
      await addOfferingProduct(offeringId, payload);
      setShowAddForm(false);
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error("Failed to add product", err);
      alert("Failed to add product. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setSku('');
    setQuantity(1);
    setDeliveryMethod('at_event');
    setIncludedInPrice(true);
    setAddonPrice('');
    setDigitalFileUrl('');
    setImageUrl('');
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProductForDelete) return;

    try {
      await deleteOfferingProduct(offeringId, selectedProductForDelete.productId || selectedProductForDelete.id);
      setSelectedProductForDelete(null);
      fetchProducts();
    } catch (err) {
      console.error("Failed to delete product", err);
      alert("Failed to remove product. Please try again.");
    }
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'pre_ship': return 'Pre-ship';
      case 'at_event': return 'At Event';
      case 'digital': return 'Digital';
      default: return method;
    }
  };

  return (
    <div className="prod-mgr-container">
      <div className="prod-mgr-header">
        <h2 className="prod-mgr-title">Bundled Products & Inventory</h2>
        {!showAddForm && (
          <button className="prod-mgr-add-btn" onClick={() => setShowAddForm(true)}>
            + Add Product
          </button>
        )}
      </div>

      {showAddForm && (
        <form className="prod-form-container" onSubmit={handleAddSubmit}>
          <h3 className="prod-form-title">Add Bundled Product</h3>
          
          <div className="prod-form-grid">
            <div className="prod-form-group">
              <label className="prod-form-label">Product Name *</label>
              <input 
                type="text" 
                required 
                placeholder="e.g. Spare Filters, Workbook, PDF Guide" 
                className="prod-form-input"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className="prod-form-group">
              <label className="prod-form-label">SKU / Item Code (Optional)</label>
              <input 
                type="text" 
                placeholder="e.g. FIL-001" 
                className="prod-form-input"
                value={sku}
                onChange={e => setSku(e.target.value)}
              />
            </div>

            <div className="prod-form-group prod-form-group--full">
              <label className="prod-form-label">Product Description</label>
              <textarea 
                placeholder="Describe what's included or the specs of the item..." 
                className="prod-form-textarea"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>

            <div className="prod-form-group">
              <label className="prod-form-label">Quantity Available *</label>
              <input 
                type="number" 
                required 
                min="0"
                className="prod-form-input"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
              />
            </div>

            <div className="prod-form-group">
              <label className="prod-form-label">Delivery Method *</label>
              <CustomDropdown
                value={deliveryMethod}
                onChange={setDeliveryMethod}
                options={DELIVERY_METHOD_OPTIONS}
              />
            </div>

            {deliveryMethod === 'digital' && (
              <div className="prod-form-group prod-form-group--full">
                <label className="prod-form-label">Digital File Download Link *</label>
                <input 
                  type="url" 
                  required 
                  placeholder="https://example.com/downloads/file.pdf" 
                  className="prod-form-input"
                  value={digitalFileUrl}
                  onChange={e => setDigitalFileUrl(e.target.value)}
                />
              </div>
            )}

            <div className="prod-form-group">
              <label className="prod-form-label">Product Image</label>
              <div className="prod-form-upload-container">
                <div className="prod-form-preview">
                  {imageUrl ? (
                    <img src={imageUrl} alt="Preview" />
                  ) : (
                    <span className="prod-form-empty-preview">📦</span>
                  )}
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  style={{ display: 'none' }}
                  id="prod-image-upload-input"
                  onChange={handleImageUpload}
                />
                <button 
                  type="button" 
                  className="prod-form-btn-cancel" 
                  style={{ padding: '6px 14px', fontSize: '13px' }}
                  onClick={() => document.getElementById('prod-image-upload-input').click()}
                  disabled={isUploadingImage}
                >
                  {isUploadingImage ? 'Uploading...' : 'Choose Image'}
                </button>
              </div>
            </div>

            <div className="prod-form-group" style={{ justifyContent: 'center' }}>
              <label className="prod-form-toggle-row">
                <input 
                  type="checkbox" 
                  className="prod-form-checkbox"
                  checked={includedInPrice}
                  onChange={e => setIncludedInPrice(e.target.checked)}
                />
                <span className="prod-form-label" style={{ cursor: 'pointer' }}>Included in Offering Price</span>
              </label>
            </div>

            {!includedInPrice && (
              <div className="prod-form-group animate-fade-in">
                <label className="prod-form-label">Add-on Price ($) *</label>
                <input 
                  type="number" 
                  step="0.01" 
                  min="0" 
                  required
                  placeholder="25.00" 
                  className="prod-form-input"
                  value={addonPrice}
                  onChange={e => setAddonPrice(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="prod-form-actions">
            <button 
              type="button" 
              className="prod-form-btn-cancel"
              onClick={() => { setShowAddForm(false); resetForm(); }}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="prod-form-btn-save"
              disabled={isSubmitting || isUploadingImage}
            >
              {isSubmitting ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div className="off-create-spinner" style={{ margin: '0 auto 10px' }}></div>
          <p style={{ color: 'rgba(255,255,255,0.6)' }}>Loading bundled products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="prod-empty-state">
          <p>No products bundled with this offering yet. Click "+ Add Product" to include manuals, physical gear, or digital documents.</p>
        </div>
      ) : (
        <div className="prod-mgr-grid">
          {products.map((prod) => (
            <div key={prod.productId || prod.id} className="prod-card">
              <div className="prod-card__img-container">
                {prod.imageUrl ? (
                  <img src={prod.imageUrl} alt={prod.name} className="prod-card__img" />
                ) : (
                  <span className="prod-card__placeholder">📦</span>
                )}
              </div>
              <h4 className="prod-card__name">{prod.name}</h4>
              {prod.sku && <span className="prod-card__sku">SKU: {prod.sku}</span>}
              {prod.description && <p className="prod-card__desc">{prod.description}</p>}
              
              <div className="prod-card__details">
                <span className="prod-badge prod-badge--method">
                  {getMethodLabel(prod.deliveryMethod)}
                </span>
                <span className="prod-badge prod-badge--price">
                  {prod.includedInPrice ? 'Included' : `+$${prod.addonPrice}`}
                </span>
                <span className="prod-badge prod-badge--qty">
                  Stock: {prod.quantity}
                </span>
              </div>

              <div className="prod-card__actions">
                <button 
                  type="button" 
                  className="prod-card__delete-btn"
                  onClick={() => setSelectedProductForDelete(prod)}
                >
                  Remove Product
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!selectedProductForDelete}
        title="Remove Bundled Product?"
        message={`Are you sure you want to remove ${selectedProductForDelete?.name} from this offering? This will stop bundling it with new bookings.`}
        confirmLabel="Remove Product"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setSelectedProductForDelete(null)}
      />
    </div>
  );
}
